import React, { useEffect, useState } from 'react';
import { Loader2, Scale, AlertTriangle, CheckCircle2, FileCode } from 'lucide-react';
import { superadminApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { isStale, minutesInWords, num } from '../lib/employmentLaw';

/**
 * Superadmin → Employment law. READ-ONLY (Rob, 21/10/2026, option a — mock-up
 * `Claude outputs/superadmin-law-panel-mock-v1.html`).
 *
 * What the app's advice is using, when it was last checked against gov.uk, and where
 * in the code it lives — Rob: *"it would be good if it says where in the code these
 * details are kept"*. The file and line come from the server, read from the source
 * each time, so they cannot go stale. Editable law was considered and turned down:
 * a typo would silently change every UK customer's advice with nothing in the way.
 *
 * ⚠️ No figure is typed here. Everything is from GET /superadmin/employment-law.
 */

// Where each figure shows up. Screens, not code — this is the list a superadmin needs
// to know what a change would reach.
const USED_ON = [
  'Sign-up → Leave policy — the tick or "N days below" line',
  'Settings → Leave policy — who is below their own minimum',
  "Each employee's form — their own minimum and the working",
  'The working-hours editor — the rest-break warning',
];

const Row = ({ label, children }) => (
  <tr className="border-t border-border">
    <td className="py-2.5 pr-4 align-top text-sm text-muted-foreground w-[42%]">{label}</td>
    <td className="py-2.5 align-top text-sm">{children}</td>
  </tr>
);

const Pack = ({ pack }) => {
  const al = pack.annual_leave;
  const rb = pack.rest_breaks;
  const stale = isStale(pack.checked_on);
  return (
    <div className="card-hive p-6">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h3 className="font-heading text-base font-semibold">{pack.name}</h3>
        {stale ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 bg-amber-500/10 text-amber-700 border border-amber-500/40">
            <AlertTriangle className="w-3 h-3" />
            {pack.checked_on
              ? `last checked ${formatDateGB(pack.checked_on)} — over a year ago, ask Claude to re-check`
              : 'never checked against its sources'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2 py-0.5 bg-emerald-500/10 text-emerald-700 border border-emerald-500/30">
            <CheckCircle2 className="w-3 h-3" /> checked against gov.uk {formatDateGB(pack.checked_on)}
          </span>
        )}
      </div>
      <table className="w-full">
        <tbody>
          {al && (
            <>
              <Row label="Paid holiday">
                <strong>{num(al.weeks)} weeks</strong> a year, of the person's own week
                <div className="text-xs text-muted-foreground mt-0.5">
                  e.g. 3 days a week × {num(al.weeks)} = {num(3 * al.weeks)} days
                </div>
              </Row>
              {al.cap_days != null && (
                <Row label="Cap">
                  <strong>{num(al.cap_days)} days</strong> — a 6-day worker still gets {num(al.cap_days)}, not {num(6 * al.weeks)}
                </Row>
              )}
            </>
          )}
          {pack.public_holidays_may_be_included != null && (
            <Row label="Can bank holidays count towards it?">
              {pack.public_holidays_may_be_included
                ? <><strong>Yes</strong> — the employer's choice (each company's Included / On top setting)</>
                : <><strong>No</strong> — they sit on top of the minimum</>}
            </Row>
          )}
          {rb && (
            <>
              <Row label="Rest break, adults">
                <strong>{minutesInWords(rb.adult_required_minutes)}</strong> when the day is over {minutesInWords(rb.adult_threshold_minutes)}
              </Row>
              {rb.young_required_minutes != null && (
                <Row label="Rest break, under 18">
                  <strong>{minutesInWords(rb.young_required_minutes)}</strong> when the day is over {minutesInWords(rb.young_threshold_minutes)}
                </Row>
              )}
            </>
          )}
          <Row label="Sources">
            {al?.source_url && <a href={al.source_url} target="_blank" rel="noreferrer" className="underline text-primary">holiday entitlement</a>}
            {al?.source_url && rb?.source_url && ' · '}
            {rb?.source_url && <a href={rb.source_url} target="_blank" rel="noreferrer" className="underline text-primary">rest breaks</a>}
          </Row>
        </tbody>
      </table>
    </div>
  );
};

const EmploymentLawPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    superadminApi.getEmploymentLaw()
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  return (
    <div className="space-y-4">
      <div className="card-hive p-6">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Scale className="w-4 h-4" /> Employment law
        </h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-3xl">
          The legal figures the app uses for its advice. Read-only — changing one is a code
          change (see the last card). <strong className="text-foreground">Nothing here moves
          anyone's balance</strong>: these only drive the "meets the minimum" ticks, the amber
          warnings and the rest-break hints.
        </p>
      </div>

      {error && (
        <div className="card-hive p-6 text-sm text-destructive">
          Couldn't load the figures. If you've just updated, restart the backend and try again.
        </div>
      )}
      {!data && !error && (
        <div className="card-hive p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      )}

      {data && (
        <>
          {data.packs.map((p) => <Pack key={p.code} pack={p} />)}

          <div className="card-hive p-6">
            <h3 className="font-heading text-base font-semibold mb-2">Where you'll see these figures</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {USED_ON.map((u) => <li key={u}>{u}</li>)}
            </ul>
          </div>

          <div className="card-hive p-6">
            <h3 className="font-heading text-base font-semibold mb-2">Every other country</h3>
            <p className="text-sm">
              No figures held, so the app <strong>says nothing</strong> about the law there — on
              purpose. Showing a French company the UK's {num(data.packs.find((p) => p.code === 'GB')?.annual_leave?.cap_days ?? '')} days would be worse than silence.
            </p>
          </div>

          <div className="card-hive p-6">
            <h3 className="font-heading text-base font-semibold mb-3 flex items-center gap-2">
              <FileCode className="w-4 h-4" /> Where this lives in the code
            </h3>
            <div className="rounded-lg bg-slate-900 text-slate-100 p-4 font-mono text-[13px] leading-relaxed overflow-x-auto" data-testid="law-where">
              <div className="text-slate-400"># the figures</div>
              {data.packs.map((p) => (
                <div key={p.code}>
                  {p.where.path}{p.where.line ? ` — line ${p.where.line}` : ''} <span className="text-slate-400">({p.name})</span>
                </div>
              ))}
              <div className="text-slate-400 mt-2"># the sums that use them</div>
              {data.sums.map((s) => (
                <div key={s.name}>{s.path} — line {s.line}, <strong>{s.name}</strong></div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              The line numbers are read from the files each time this page opens, so they can't go out of date.
            </p>
            <div className="mt-3 text-sm text-muted-foreground border-l-4 border-amber-400 bg-amber-500/5 rounded-r-lg px-3 py-2">
              If gov.uk ever disagrees with this page: tell Claude. It's one change in that file
              plus a deploy, and the test suite checks the sums before it ships.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmploymentLawPanel;
